import { defineStore, storeToRefs } from 'pinia'
import {
    type App,
    computed,
    type ComputedRef,
    inject,
    ref,
    type Ref,
} from 'vue'
import type { LoginResponseSuccessFragment, MeFragment } from '@/graphql/api'
import type { Email } from '@/types/email.ts'

export interface AuthPlugin {
    user: Readonly<Ref<MeFragment | undefined | null>>
    isLogged: ComputedRef<boolean>
    setUser: (u: MeFragment | null | undefined) => void
    setAuth: (auth: LoginResponseSuccessFragment | null) => void
    getIntercomUser(): IntercomUser | null
}

const key = 'plugin.auth'

export function installAuth(app: App): AuthPlugin {
    const plugin = createPlugin()

    app.provide(key, plugin)

    return plugin
}

export function useAuth(): AuthPlugin {
    return inject(key) as AuthPlugin
}

function createPlugin(): AuthPlugin {
    // eslint-disable-next-line vue-composable/composable-placement
    const { user } = storeToRefs(useAuthTemporaryStore())

    const isLogged = computed(() => Boolean(user.value))

    const setUser = (u: MeFragment | null | undefined) => {
        if (!u) {
            user.value = null

            return
        }

        user.value = u
    }

    const setAuth = (auth: LoginResponseSuccessFragment | null) => {
        if (!auth) {
            setUser(null)

            return
        }

        setUser(auth.user)
    }

    return {
        user,
        isLogged,
        setUser,
        setAuth,
        getIntercomUser(): IntercomUser | null {
            return getIntercomUser(user.value)
        },
    }
}

const useAuthTemporaryStore = defineStore('authTemp', () => {
    const user = ref<MeFragment | undefined | null>(undefined)

    return {
        user,
    }
})

function getIntercomUser(
    user: MeFragment | null | undefined
): IntercomUser | null {
    if (!user || user.isConsultant) {
        return null
    }

    return {
        name: `${user.firstName} ${user.lastName}`,
        email: user.email,
        created_at: user.createdAt,
        consultant: user.customer?.consultant?.user.email,
    }
}

export type IntercomUser = {
    name: string
    email: Email
    created_at: string
    consultant?: Email
}
